import { useEffect, useRef } from "react";
import { clearArtifactFocus } from "../slices/appStateSlice";
import type { ArtifactKind } from "../types/index.type";
import { useAppDispatch, useAppSelector } from "./redux-hooks";

const HIGHLIGHT_MS = 1800;

/**
 * Claude-style "slide open to the artifact" focus behavior.
 *
 * Attach the returned ref to the wrapper of an artifact section in the
 * right-hand notes panel. When `appState.artifactFocus` matches one of
 * `kinds` for the currently open note, the section scrolls into view
 * and pulses with the `artifact-highlight` animation (global.scss),
 * then the focus state clears.
 */
export const useArtifactFocus = (kinds: ArtifactKind | ArtifactKind[]) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const artifactFocus = useAppSelector((state) => state.appState.artifactFocus);
  const openAudioFileId = useAppSelector((state) => state.notes.notesDetails?.id);

  const kindKey = Array.isArray(kinds) ? kinds.join(",") : kinds;

  useEffect(() => {
    const el = ref.current;
    if (!artifactFocus || !el) return;
    if (!kindKey.split(",").includes(artifactFocus.kind)) return;
    if (artifactFocus.audioFileId !== openAudioFileId) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("artifact-highlight");
    const timer = setTimeout(() => {
      el.classList.remove("artifact-highlight");
      dispatch(clearArtifactFocus());
    }, HIGHLIGHT_MS);

    return () => {
      clearTimeout(timer);
      el.classList.remove("artifact-highlight");
    };
  }, [artifactFocus, openAudioFileId, kindKey, dispatch]);

  return ref;
};
